
import { useEffect, useState, useCallback } from 'react'
import { GetSurvey, SaveSurveyResults } from '../../../services/SurveyService'
import { type UploadFilesEvent, type Model } from 'survey-core'
import { registerCustomQuestions } from './questions'
import 'survey-core/survey-core.min.css'
import { SurveyDisplay } from './SurveyDisplay'
import { type SurveyType } from '../../../models/SurveySchema'
import { UploadFile } from '../../../services/FileService'

registerCustomQuestions()

interface Props {
  annualPlanId: number
  surveyType: SurveyType
}

export function STARTSurveyDisplay ({
  annualPlanId,
  surveyType
}: Props): JSX.Element {
  const [surveyJson, setSurveyJson] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [answers, setAnswers] = useState<Record<string, any> | undefined>(undefined)

  const saveSurvey = useCallback(async (survey: Model): Promise<void> => {
    const data = survey.data
    survey.getAllQuestions().forEach(q => {
      const question = q as any
      if (question.extraInfo !== undefined) {
        data[q.name + '_Comment'] = (question.extraInfo != null && question.extraInfo !== '') ? question.extraInfo : null
      }
      if (question.uploadedFileName !== undefined) {
        data[q.name + '_File'] = (question.uploadedFileName != null && question.uploadedFileName !== '') ? question.uploadedFileName : null
      }
      if (question.uploadedFileKey !== undefined) {
        data[q.name + '_FileKey'] = (question.uploadedFileKey != null && question.uploadedFileKey !== '') ? question.uploadedFileKey : null
      }
    })
    await SaveSurveyResults({
      schemaId: surveyType,
      surveyAnswers: JSON.stringify(data),
      annualPlanId
    })
  }, [annualPlanId])

  const onComplete = useCallback(async (survey: Model, options: any): Promise<void> => {
    options.showSaveInProgress()
    await saveSurvey(survey)
  }, [saveSurvey])

  const onLoad = useCallback(async (survey: Model): Promise<void> => {
    survey.getAllQuestions().forEach(question => {
      if (answers == null) return
      if (answers[question.name + '_Comment'] != null) {
        question.extraInfo = answers[question.name + '_Comment']
      }
      if (answers[question.name + '_File'] != null) {
        question.uploadedFileName = answers[question.name + '_File']
      }
      if (answers[question.name + '_FileKey'] != null) {
        question.uploadedFileKey = answers[question.name + '_FileKey']

        let saveTimeout: NodeJS.Timeout
        question.onPropertyChanged.add((_sender, _options) => {
          clearTimeout(saveTimeout)
          saveTimeout = setTimeout(() => {
            void saveSurvey(survey)
          }, 500)
        })
      }
    })

    survey.onUploadFiles.add((_sender: Model, { question, files }: UploadFilesEvent) => {
      return files.map(async (file: File) => {
        try {
          const key = await UploadFile(file)
          question.uploadedFileKey = key
          question.uploadedFileName = file.name
          return {
            file,
            content: key
          }
        } catch (e) {
          console.error('Failed to upload file', e)
          return null
        }
      })
    })
  }, [answers, saveSurvey])

  useEffect(() => {
    void loadSchema()
  }, [])

  const loadSchema = async (): Promise<void> => {
    await GetSurvey(surveyType, annualPlanId)
      .then(schema => {
        const schemaJson = JSON.parse(schema.content)
        setSurveyJson(schemaJson)
        if (schema.answers != null) {
          setAnswers(JSON.parse(schema.answers))
        }
      })
      .catch(() => { console.error('Error fetching survey schema') })
      .finally(() => { setLoading(false) })
  }

  if (loading) return <div>Loading...</div>

  return (
    <div style={{ width: '100%' }}>
      {surveyJson !== null
        ? <SurveyDisplay surveyJson={surveyJson} onComplete={onComplete} answers={answers} onLoad={onLoad}/>
        : <div>No schema found</div>
      }
    </div>

  )
}
